import { QueryClient, QueryKey } from "@tanstack/react-query";

const ONE_DAY_MS = 1000 * 60 * 60 * 24;
const gatewayUserScopedMetaKey = 'gatewayUserScoped';

export const gatewayUserScopedQueryOptions = {
    gcTime: ONE_DAY_MS,
    networkMode: 'always' as const,
    retry: false,
    meta: {
        [gatewayUserScopedMetaKey]: true
    }
};

export const gatewayRealtimeQueryOptions = {
    ...gatewayUserScopedQueryOptions,
    staleTime: Infinity
};

export type GatewayObjectUpdate<TBody = Record<string, any>> = {
    type: 'create' | 'update' | 'delete';
    body?: TBody;
};

export type InfinitePages<T> = {
    pages: T[][];
    pageParams: unknown[];
};

export function invalidateGatewayUserScopedQueries(queryClient: QueryClient) {
    console.info('(gatewayQueryUtils) Invalidating user-scoped gateway queries...');
    queryClient.invalidateQueries({
        predicate: (query) => query.meta?.[gatewayUserScopedMetaKey] === true
    });
}

export function updateMatchingQueries(
    queryClient: QueryClient,
    queryKey: QueryKey,
    updateQueryData: (queryKey: readonly unknown[]) => void
) {
    queryClient.getQueryCache().findAll({ queryKey }).forEach((query) => {
        updateQueryData(query.queryKey);
    });
}

export function getNumberQueryKeyTail(queryKey: readonly unknown[]) {
    const lastQueryKeyPart = queryKey[queryKey.length - 1];

    return typeof lastQueryKeyPart === 'number' ? lastQueryKeyPart : undefined;
}

export function mergeUpdateBodyIntoCachedObject<T extends Record<string, any>>(
    current: T | null | undefined,
    update: GatewayObjectUpdate<Partial<T>>,
    logPrefix?: string
) {
    if (!current) {
        console.log(`${logPrefix ?? 'objectUpdate'} (ignored)`, update);
        return current;
    }

    if (update.type !== 'update' || !update.body) {
        console.log(`${logPrefix ?? 'objectUpdate'} (no match)`, update);
        return current;
    }

    console.log(`${logPrefix ?? 'objectUpdate'} (relevant update)`, update);
    // Some cached objects are class instances (e.g. DeviceEvent) whose getters
    // live on the prototype, so the merged copy must keep that prototype.
    return Object.assign(Object.create(Object.getPrototypeOf(current)), current, update.body);
}

export function infiniteQueryHasItem<T>(
    queryData: InfinitePages<T> | undefined,
    predicate: (item: T) => boolean
) {
    return !!queryData?.pages?.some(page => page.some(predicate));
}

export function removeInfiniteQueryItem<T>(
    queryData: InfinitePages<T> | undefined,
    predicate: (item: T) => boolean,
    logPrefix?: string
) {
    if (!queryData || !infiniteQueryHasItem(queryData, predicate)) {
        console.log(`${logPrefix ?? 'infiniteQueryUpdate'} (ignored)`);
        return queryData;
    }

    console.log(`${logPrefix ?? 'infiniteQueryUpdate'} (relevant delete)`);
    return {
        pages: queryData.pages.map(page => page.filter(item => !predicate(item))),
        pageParams: queryData.pageParams
    };
}

export function prependInfiniteQueryItem<T>(
    queryData: InfinitePages<T> | undefined,
    item: T,
    logPrefix?: string
) {
    const pages = queryData?.pages;

    if (!queryData || !pages?.length) {
        console.log(`${logPrefix ?? 'infiniteQueryUpdate'} (ignored)`, item);
        return queryData;
    }

    console.log(`${logPrefix ?? 'infiniteQueryUpdate'} (relevant create)`, item);
    return {
        pages: [[item, ...pages[0]], ...pages.slice(1)],
        pageParams: queryData.pageParams
    };
}
